import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, MapPin, BriefcaseBusiness } from "lucide-react";
import Section, { SectionHeading } from "@/components/Section";
import CtaBanner from "@/components/CtaBanner";
import { fetchRoles } from "@/lib/api";

export default function Careers() {
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    fetchRoles()
      .then((r) => { if (!cancelled) setRoles(r); })
      .catch(() => { if (!cancelled) setRoles([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  return (
    <div data-testid="page-careers" className="pt-[72px]">
      <Section id="careers-intro" bg="white">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
          <div className="lg:col-span-7">
            <SectionHeading
              eyebrow="Careers · OneHill"
              title={<>Join the team powering <span className="text-[#99CC00]">quick commerce expansion.</span></>}
              sub="Hands-on operating roles across dark stores, supply chain, MIS, HR and cluster leadership. We move fast, document everything and reward execution."
            />
          </div>
          <div className="lg:col-span-5 rounded-[2rem] bg-[#F7F8F5] p-7">
            <BriefcaseBusiness className="w-10 h-10 text-[#99CC00]" />
            <div className="mt-5 text-xs font-mono uppercase tracking-[0.22em] text-[#99CC00] font-black">Hiring · Multi-City</div>
            <p className="mt-3 text-sm text-neutral-600">
              Selected for interviews based on operational mindset, ground-floor experience and a bias for execution.
            </p>
            <ul className="mt-6 grid grid-cols-2 gap-3 text-sm">
              <li className="rounded-2xl border border-neutral-200 bg-white p-4">
                <div className="text-[10px] font-mono uppercase tracking-[0.22em] text-neutral-500 font-bold">Cities</div>
                <div className="font-display text-2xl font-black mt-1">10+</div>
              </li>
              <li className="rounded-2xl border border-neutral-200 bg-white p-4">
                <div className="text-[10px] font-mono uppercase tracking-[0.22em] text-neutral-500 font-bold">Open Roles</div>
                <div className="font-display text-2xl font-black mt-1">7</div>
              </li>
            </ul>
          </div>
        </div>
      </Section>

      <Section id="careers-roles" bg="soft">
        <SectionHeading eyebrow="Open Roles" title={<>Active <span className="text-[#99CC00]">openings.</span></>} />
        <div className="mt-10 grid gap-4" data-testid="careers-role-list">
          {loading ? (
            Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="rounded-3xl bg-white border border-neutral-200 p-6 animate-pulse">
                <div className="h-5 w-1/3 bg-neutral-200" />
                <div className="mt-3 h-3 w-1/2 bg-neutral-100" />
              </div>
            ))
          ) : roles.length === 0 ? (
            <div className="rounded-3xl bg-white border border-neutral-200 p-8 text-neutral-500">No open roles right now. Please check back soon.</div>
          ) : (
            roles.map((r, i) => (
              <motion.div
                key={r.slug}
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.4, delay: i * 0.04 }}
                data-testid={`role-row-${r.slug}`}
                className="rounded-3xl bg-white border border-neutral-200 p-6 lg:p-7 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 shadow-sm hover:shadow-xl hover:border-[#99CC00]/40 transition"
              >
                <div className="flex items-start gap-4">
                  <div className="inline-flex p-3 rounded-2xl" style={{ background: "rgba(153,204,0,0.14)" }}>
                    <BriefcaseBusiness className="w-7 h-7 text-[#99CC00]" />
                  </div>
                  <div>
                    <div className="font-display font-black text-[#1F1F1F] text-xl lg:text-2xl">{r.title}</div>
                    <div className="mt-1 text-sm text-neutral-500 flex flex-wrap items-center gap-3">
                      <span className="inline-flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5 text-[#99CC00]" />{r.location}</span>
                      <span className="font-mono text-[11px]">· {r.type}</span>
                    </div>
                  </div>
                </div>
                <Link
                  to={`/contact?role=${encodeURIComponent(r.title)}&type=Careers`}
                  data-testid={`role-apply-${r.slug}`}
                  className="inline-flex items-center justify-center gap-2 rounded-full bg-[#99CC00] text-[#1F1F1F] px-6 py-3 text-sm font-black hover:bg-[#B5E000] transition-colors"
                >
                  Apply Now <ArrowRight className="w-4 h-4" />
                </Link>
              </motion.div>
            ))
          )}
        </div>
      </Section>

      <CtaBanner eyebrow="Apply" title="Don’t see your role? Send us your profile." cta="Reach out" />
    </div>
  );
}
